import { Controller } from "@hotwired/stimulus"

// Quick selector - command-K style selector with filtering and keyboard navigation
// Mobile-friendly with bottom sheet on small screens, centered panel on desktop
export default class extends Controller {
  static targets = ["backdrop", "panel", "input", "list", "item", "header"]
  static values = {
    open: { type: Boolean, default: false },
    id: String
  }

  connect() {
    this.selectedIndex = 0
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleGlobalOpen = this.handleGlobalOpen.bind(this)

    // Listen for global open events
    document.addEventListener("quick-selector:open", this.handleGlobalOpen)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("quick-selector:open", this.handleGlobalOpen)
  }

  handleGlobalOpen(event) {
    // Only respond if this selector's ID matches
    if (event.detail?.id === this.idValue) {
      this.open()
    }
  }

  // Called from button via global dispatch
  static openById(id) {
    document.dispatchEvent(new CustomEvent("quick-selector:open", { detail: { id } }))
  }

  open(event) {
    event?.preventDefault()
    this.openValue = true
    this.backdropTarget.classList.remove("hidden")
    this.panelTarget.classList.remove("hidden")

    // Focus input after animation frame
    requestAnimationFrame(() => {
      this.inputTarget.value = ""
      this.inputTarget.focus()
      this.filter()
    })

    document.addEventListener("keydown", this.handleKeydown)
  }

  close(event) {
    event?.preventDefault()
    this.openValue = false
    this.backdropTarget.classList.add("hidden")
    this.panelTarget.classList.add("hidden")
    document.removeEventListener("keydown", this.handleKeydown)
  }

  handleKeydown(event) {
    if (!this.openValue) return

    switch (event.key) {
      case "Escape":
        event.preventDefault()
        this.close()
        break
      case "ArrowDown":
        event.preventDefault()
        this.moveSelection(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveSelection(-1)
        break
      case "Enter":
        event.preventDefault()
        this.selectCurrent()
        break
    }
  }

  filter() {
    const query = this.inputTarget.value.toLowerCase().trim()

    // Filter items
    this.itemTargets.forEach((item) => {
      const text = item.dataset.filterText?.toLowerCase() || item.textContent.toLowerCase()
      const matches = query === "" || text.includes(query)
      item.classList.toggle("hidden", !matches)
    })

    // Show/hide section headers based on whether they have visible items
    if (this.hasHeaderTarget) {
      this.headerTargets.forEach((header) => {
        const section = header.dataset.section
        if (!section) return

        // Find items in this section
        const sectionItems = this.itemTargets.filter(item => item.dataset.section === section)
        const hasVisibleItems = sectionItems.some(item => !item.classList.contains("hidden"))
        header.classList.toggle("hidden", !hasVisibleItems)
      })
    }

    // Land on the current selection when it's visible (e.g. on open), otherwise
    // highlight the first match.
    const visibleItems = this.itemTargets.filter(item => !item.classList.contains("hidden"))
    const currentIndex = visibleItems.findIndex(item => item.dataset.current === "true")
    this.selectedIndex = currentIndex >= 0 ? currentIndex : 0
    this.updateSelectionDisplay()
  }

  moveSelection(direction) {
    const visibleItems = this.itemTargets.filter(item => !item.classList.contains("hidden"))
    if (visibleItems.length === 0) return

    this.selectedIndex += direction
    if (this.selectedIndex < 0) this.selectedIndex = visibleItems.length - 1
    if (this.selectedIndex >= visibleItems.length) this.selectedIndex = 0

    this.updateSelectionDisplay()
  }

  updateSelectionDisplay() {
    const visibleItems = this.itemTargets.filter(item => !item.classList.contains("hidden"))

    visibleItems.forEach((item, index) => {
      const highlighted = index === this.selectedIndex
      // Coral inset ring (matches the app's selected-item treatment) instead of a
      // flat tan fill, so it reads cleanly on both plain and color-filled rows.
      item.classList.toggle("ring-2", highlighted)
      item.classList.toggle("ring-inset", highlighted)
      item.classList.toggle("ring-brand-accent", highlighted)
      if (highlighted) item.scrollIntoView({ block: "nearest" })
    })
  }

  selectCurrent() {
    const visibleItems = this.itemTargets.filter(item => !item.classList.contains("hidden"))
    const selected = visibleItems[this.selectedIndex]

    if (selected) {
      // Find the link inside and click it
      const link = selected.tagName === "A" ? selected : selected.querySelector("a")
      if (link) {
        link.click()
      }
    }
  }

  selectItem(event) {
    // Item was clicked directly, let the link handle navigation
    this.close()
  }
}
